import { useState } from 'react';
import styled from 'styled-components';
import { MediaImage } from '@elfsight/widget-media';
import { CardColumn, CardColumns } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import type { Job } from '../../../types';
import { coverUrl } from '../../utils';
import {
  Buttons,
  Description,
  getTitleBlockSettings,
  Skills,
  TitleBlock
} from './parts';
import { ButtonsComponent } from './parts/Buttons';

const COVER_SIZE = '260px';

interface HorizontalItemProps {
  data: Job;
  cardWidth?: number;
}

export function HorizontalItem({ data, cardWidth }: HorizontalItemProps) {
  const { title, link, cover } = data;
  const {
    isShowApplyButton,
    isShowLearnMoreButton,
    isShowDescription,
    isShowSkills,
    isShowJobCover,
    jobs
  } = useSettings();

  const [titleHeight, setTitleHeight] = useState(0);

  const img = coverUrl(cover, jobs);
  const withCover = !!(isShowJobCover && img);

  return (
    <CardColumns>
      <Left style={{ width: withCover ? COVER_SIZE : '75%' }}>
        {withCover ? (
          <MediaImage
            src={img}
            alt={title}
            ratio={1}
            style={{ flexGrow: '1' }}
          />
        ) : (
          <Information
            data={data}
            titleHeight={titleHeight}
            setTitleHeight={setTitleHeight}
            withCover={withCover}
            cardWidth={cardWidth}
          />
        )}
      </Left>
      <Right
        style={{ width: withCover ? `calc(100% - ${COVER_SIZE})` : '25%' }}
      >
        {withCover && (
          <Information
            data={data}
            titleHeight={titleHeight}
            setTitleHeight={setTitleHeight}
            withCover={withCover}
            cardWidth={cardWidth}
          />
        )}

        {((isShowApplyButton && link) || isShowLearnMoreButton) && (
          <Buttons
            data={data}
            direction={withCover ? 'row-reverse' : 'column'}
            titleHeight={titleHeight}
            isShowApplyButton={isShowApplyButton}
            isShowLearnMoreButton={isShowLearnMoreButton}
            isShowDescription={isShowDescription}
            isShowSkills={isShowSkills}
            cardWidth={cardWidth}
          />
        )}
      </Right>
    </CardColumns>
  );
}

interface InformationProps {
  data: Job;
  titleHeight: number;
  setTitleHeight: Function;
  withCover: boolean;
  cardWidth?: number;
}

function Information({
  data,
  titleHeight,
  setTitleHeight,
  cardWidth,
  withCover
}: InformationProps) {
  const settings = useSettings();
  const {
    jobTextColor,
    isShowDescription,
    jobLinkColor,
    jobDescriptionStyle,
    isShowSkills,
    jobSkillsStyle
  } = settings;

  const { description, skills } = data;

  return (
    <div>
      <TitleBlock
        {...data}
        {...getTitleBlockSettings(settings)}
        salaryAlign="flex-start"
        color={jobTextColor}
        setTitleHeight={setTitleHeight}
        titleHeight={titleHeight}
        withCover={withCover}
        cardWidth={cardWidth || 0}
      />

      {isShowDescription && description && (
        <Description
          color={jobTextColor}
          linkColor={jobLinkColor}
          fontSize={jobDescriptionStyle.fontSize}
        >
          {description}
        </Description>
      )}

      {isShowSkills && skills?.length > 0 && (
        <Skills
          items={skills}
          color={jobTextColor}
          fontSize={jobSkillsStyle.fontSize}
        />
      )}
    </div>
  );
}

const Left = styled(CardColumn)``;

const Right = styled(CardColumn)`
  justify-content: space-between;

  ${ButtonsComponent} {
    flex-shrink: 0;

    &:last-child:first-child {
      margin: auto 0;
    }
  }
`;
